import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, MapPin, Store, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ColorSizeDisplay } from '../components/ColorSizePicker';
import { formatItemPrice } from '../lib/currency';
import { usePageHeader } from '../hooks/usePageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [activeSuppliers, setActiveSuppliers] = useState(null); // null = still loading; Set of lowercased active supplier names
  const categoryFromUrl = searchParams.get('category');
  const brandFromUrl = searchParams.get('brand');
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || 'featured');
  const [selectedBrand, setSelectedBrand] = useState(brandFromUrl || '');
  const [addedItems, setAddedItems] = useState({}); // Track "Added" state per product
  const { addToCart } = useCart();
  const header = usePageHeader('new-gear', { heading: 'New Gear', subheading: 'Hero gear that works. Selected for reliability, longevity, and real-world use.' });

  const categories = [
    { value: 'featured', label: 'Featured' },
    { value: 'all', label: 'All' },
    { value: 'regulators', label: 'Regulators' },
    { value: 'bcds', label: 'BCDs' },
    { value: 'computers', label: 'Dive Computers' },
    { value: 'wetsuits', label: 'Wetsuits' },
    { value: 'masks', label: 'Masks' },
    { value: 'fins', label: 'Fins' },
    { value: 'snorkels', label: 'Snorkels' },
    { value: 'boots', label: 'Boots' },
    { value: 'gloves', label: 'Gloves' },
    { value: 'wetsuits', label: 'Wetsuits' },
    { value: 'drysuits', label: 'Drysuits' },
    { value: 'cylinders', label: 'Cylinders' },
    { value: 'knives', label: 'Knives' },
    { value: 'torches', label: 'Torches' },
    { value: 'bags', label: 'Bags' },
    { value: 'buoys-reels', label: 'Buoys & Reels' },
    { value: 'instruments', label: 'Instruments' },
    { value: 'octos', label: "Octo's" },
    { value: 'snorkeling', label: 'Snorkeling' },
    { value: 'freediving', label: 'Freediving' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'sets', label: 'Sets' },
  ];

  // Update selectedCategory/Brand when URL changes (e.g., from homepage category links)
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    const brandFromUrl = searchParams.get('brand') || '';
    if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
    if (brandFromUrl !== selectedBrand) {
      setSelectedBrand(brandFromUrl);
    }
  }, [searchParams]);

  // Fetch available brands once
  useEffect(() => {
    axios.get(`${API_URL}/product-brands`)
      .then((res) => setBrands(res.data || []))
      .catch(() => setBrands([]));
  }, []);

  // Fetch active suppliers — used to conditionally hide each "Try Before You Buy"
  // section. The /api/suppliers endpoint already excludes hidden / disabled /
  // deleted suppliers, so we just match by name.
  useEffect(() => {
    axios.get(`${API_URL}/suppliers`)
      .then((res) => {
        const names = new Set((res.data || []).map((s) => (s.name || '').toLowerCase()));
        setActiveSuppliers(names);
      })
      .catch(() => setActiveSuppliers(new Set()));
  }, []);

  // Admin-controlled master toggle for the "Try Before You Buy" panel.
  const [tryBeforeYouBuyEnabled, setTryBeforeYouBuyEnabled] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/site-content`)
      .then((res) => {
        const v = res.data?.try_before_you_buy_enabled;
        setTryBeforeYouBuyEnabled(String(v ?? 'true').toLowerCase() !== 'false');
      })
      .catch(() => setTryBeforeYouBuyEnabled(true));
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedBrand]);

  // Update URL when category is changed via button click
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const params = { category };
    if (selectedBrand) params.brand = selectedBrand;
    setSearchParams(params);
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    // If user picks a brand while on "Featured" (which is brand-agnostic),
    // auto-switch to "All" so they immediately see that brand's catalog.
    let nextCategory = selectedCategory;
    if (brand && selectedCategory === 'featured') {
      nextCategory = 'all';
      setSelectedCategory('all');
    }
    const params = { category: nextCategory };
    if (brand) params.brand = brand;
    setSearchParams(params);
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/products?category=${selectedCategory}`;
      if (selectedBrand) url += `&brand=${encodeURIComponent(selectedBrand)}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    // Prevent rapid double-clicks
    if (addedItems[product.id]) {
      toast.info('Item already in cart! Go to cart to update quantity.');
      return;
    }
    
    addToCart(product);
    
    // Show "Added" state for 3 seconds
    setAddedItems(prev => ({ ...prev, [product.id]: true }));
    toast.success(`${product.name} added to cart!`);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setAddedItems(prev => ({ ...prev, [product.id]: false }));
    }, 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342] mb-4">
            {header.heading}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {header.subheading}
          </p>
        </div>

        {/* Store Location Notice — only shown when at least one supplier is active AND admin hasn't hidden the panel */}
        {tryBeforeYouBuyEnabled && activeSuppliers && (activeSuppliers.has('aqua divers') || activeSuppliers.has('manex & power marine')) && (
        <div className="bg-[#0A2342] text-white p-6 rounded-lg mb-8">
          <div className="flex items-start gap-3 mb-4">
            <Store className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Try Before You Buy</h3>
              {activeSuppliers.has('aqua divers') && (
                <p className="text-gray-300 text-sm">All new Mares gear can be fitted and viewed at <strong className="text-white font-semibold">Aqua Divers</strong>:</p>
              )}
            </div>
          </div>
          {activeSuppliers.has('aqua divers') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-9">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold">Pretoria</p>
                <p className="text-gray-300">Shop G145 Parkview Shopping Centre,</p>
                <p className="text-gray-300">24 Garsfontein Rd, Pretoria</p>
                <p className="text-[#FF6B35] mt-1">+2712 880 5300</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold">Randburg</p>
                <p className="text-gray-300">Unit C5, Northland Deco Park,</p>
                <p className="text-gray-300">Design Blvd, Hoogland, Randburg</p>
                <p className="text-[#FF6B35] mt-1">+2711 794 9444</p>
              </div>
            </div>
          </div>
          )}

          {activeSuppliers.has('manex & power marine') && (
          <div className={`${activeSuppliers.has('aqua divers') ? 'mt-6 pt-6 border-t border-white/10' : ''}`}>
            <p className="text-gray-300 text-sm mb-4 ml-9">All new Aqualung and Apex gear can be fitted and viewed at <strong className="text-white font-semibold">Manex &amp; Power Marine</strong>:</p>
            <div className="ml-9 flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold">Rosebank</p>
                <p className="text-gray-300">The Travel House, 2nd Floor,</p>
                <p className="text-gray-300">6 Hood Ave, Rosebank</p>
                <p className="text-[#FF6B35] mt-1">+2711 721 1490</p>
              </div>
            </div>
          </div>
          )}
        </div>
        )}

        {/* Brand Filter Chips */}
        {brands.length > 0 && (
          <div className="mb-6" data-testid="brand-filter-row">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Filter by Brand</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBrandChange('')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                  selectedBrand === ''
                    ? 'bg-[#FF6B35] text-white border-[#FF6B35]'
                    : 'bg-white text-[#0A2342] border-gray-300 hover:border-[#FF6B35]'
                }`}
                data-testid="brand-chip-all"
              >
                All Brands
              </button>
              {brands.map((b) => (
                <button
                  key={b.name}
                  onClick={() => handleBrandChange(b.name)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                    selectedBrand === b.name
                      ? 'bg-[#FF6B35] text-white border-[#FF6B35]'
                      : 'bg-white text-[#0A2342] border-gray-300 hover:border-[#FF6B35]'
                  }`}
                  data-testid={`brand-chip-${b.name.toLowerCase()}`}
                >
                  {b.name} <span className="text-xs opacity-70 ml-1">({b.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={selectedCategory === category.value ? 'default' : 'outline'}
              onClick={() => handleCategoryChange(category.value)}
              className={selectedCategory === category.value ? 'bg-[#0A2342] text-white' : ''}
              data-testid={`category-${category.value}-btn`}
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-[#0A2342]/20 hover:shadow-xl transition-all duration-300 group"
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-xl font-heading font-semibold text-[#0A2342] group-hover:text-[#FF6B35] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    {product.in_stock ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200">In Stock</Badge>
                    ) : (
                      <Badge variant="destructive">Out of Stock</Badge>
                    )}
                  </div>
                  {product.description && !product.description.includes('Add product description in admin') && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {product.description}
                    </p>
                  )}
                  
                  {/* Size options */}
                  {product.sizes && product.sizes.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Sizes available:</p>
                      <div className="flex flex-wrap gap-1">
                        {product.sizes.map((size) => (
                          <span key={size} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Color swatches */}
                  {product.available_colors && product.available_colors.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Colors:</p>
                      <ColorSizeDisplay 
                        colors={product.available_colors} 
                        customColors={product.custom_colors}
                      />
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <div className="text-2xl font-mono-accent font-bold text-[#0A2342] mb-3">
                      {formatItemPrice(product.price, product.currency)}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-[10px] border-[#0A2342] text-[#0A2342]">Club 20%</Badge>
                        <span className="font-mono-accent font-semibold text-[#0A2342]">
                          {formatItemPrice(product.price * 0.8, product.currency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-[10px] border-[#FF6B35] text-[#FF6B35]">Pro 25%</Badge>
                        <span className="font-mono-accent font-semibold text-[#FF6B35]">
                          {formatItemPrice(product.price * 0.75, product.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.in_stock || addedItems[product.id]}
                    className={
                      addedItems[product.id]
                        ? "w-full bg-green-600 text-white cursor-not-allowed"
                        : "w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                    }
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    {addedItems[product.id] ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Added to Cart!
                      </>
                    ) : (
                      'Add to Cart'
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
